"use client";
import type { AvailabilityError, ResourceType } from "@gettactic/api";
import { deriveOfficeAvailabilityErrors } from "@gettactic/helpers/src/reserve/officeAvailabilityErrors";
import { getResourceTypeGroup } from "@gettactic/helpers/src/reserve/resourceTypeGroup";
import { useMapSchedulableQueries } from "@gettactic/hooks";
import { useMemo } from "react";
import { getStartEndDate } from "@/features/reserve/hooks/useReserveResources";

type UseReserveOfficeAvailabilityErrorsOptions = {
  officeId: string | null;
  resourceType: ResourceType;
  // Multi-date picker's dates, one schedulable window is requested per date.
  weekDatesStr: string[];
  startTime: Date;
  durationMins: number;
  filterParent?: string | null;
  organizer?: string | null;
  enabled?: boolean;
};

/**
 * Office-level reasons (closed hours, holidays, booking horizon) the user
 * can't reserve on any of the selected dates. The resource list already
 * loads the earliest date's window, so that one is served from cache and
 * only the extra dates cost a request.
 */
export function useReserveOfficeAvailabilityErrors({
  officeId,
  resourceType,
  weekDatesStr,
  startTime,
  durationMins,
  filterParent,
  organizer = null,
  enabled = true
}: UseReserveOfficeAvailabilityErrorsOptions): {
  officeAvailabilityErrors: AvailabilityError[];
} {
  const { nonAvailable } = useMapSchedulableQueries(
    weekDatesStr.map((date) => {
      const { startDateAndTime, endDateAndTime } = getStartEndDate(
        [date],
        startTime,
        durationMins,
        false
      );
      return {
        officeId,
        parentId: filterParent ?? null,
        startDate: startDateAndTime,
        endDate: endDateAndTime,
        organizerId: organizer,
        enabled: enabled && !!officeId
      };
    })
  );

  const officeAvailabilityErrors = useMemo(
    () =>
      deriveOfficeAvailabilityErrors(
        nonAvailable,
        getResourceTypeGroup(resourceType)
      ),
    [nonAvailable, resourceType]
  );

  return { officeAvailabilityErrors };
}
